// src/sweedMappers.js
//
// TEST HARNESS. Maps Sweed REST responses into the legacy (Dutchie) GraphQL shapes
// the existing hooks/components consume, so the app renders real Sweed data with no
// consumer changes. Used by the Sweed Apollo link in apolloClient.js.
//
// IMPORTANT: results are written into the Apollo cache, so every nested object must
// carry a `__typename` and every field the GraphQL fragments select must be present
// (use null / [] where Sweed has no data) — otherwise Apollo throws on cache write.
//
// Sweed quirks handled: list variants use `prices`, detail uses `pricing`; prices are
// dollars (legacy expects cents). Pricing is written to BOTH med and rec keys so it
// shows regardless of the configured menu type.

const toCents = (n) => (n == null ? null : Math.round(Number(n) * 100));

const firstCompound = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const c = arr[0];
    const value = Array.isArray(c.values) ? c.values[0] : c.values;
    if (value == null) return null;
    const isPct = c.uom === 'percent';
    return {
        __typename: 'Potency',
        formatted: `${value}${isPct ? '%' : 'mg'}`,
        range: String(value),
        unit: isPct ? 'PERCENTAGE' : 'MILLIGRAMS',
    };
};

const mapVariant = (item, v) => {
    const pricing = v.pricing || v.prices || {};
    const baseC = toCents(pricing.base);
    const saleC = toCents(pricing.sale);
    return {
        __typename: 'ProductVariant',
        id: v.id || `${item.id}~${v.name}`,
        option: v.name,
        quantity: v.stock ? v.stock.quantity : 0,
        priceRec: baseC,
        priceMed: baseC,
        specialPriceRec: saleC,
        specialPriceMed: saleC,
    };
};

const mapBrand = (b) =>
    b
        ? { __typename: 'Brand', id: b.id, name: b.name, description: '', imageUrl: b.imageUrl || '' }
        : null;

/**
 * Map a single Sweed product (list item OR detail) to the legacy product shape.
 * Every productFragment field is present so Apollo can cache it cleanly.
 */
export function mapSweedToProduct(item, lookups = {}) {
    if (!item) return null;
    const { categories = {}, brands = {}, strains = {} } = lookups;
    const isDetail = !!item.attributes;
    const attrs = item.attributes || {};
    const attrIds = item.attributeIds || {};

    // Category
    const category = isDetail
        ? (attrs.category && attrs.category.name) || ''
        : categories[attrIds.categoryId] || '';

    // Brand
    let brand = null;
    if (isDetail && attrs.brand) brand = mapBrand(attrs.brand);
    else if (attrIds.brandId && brands[attrIds.brandId]) brand = mapBrand(brands[attrIds.brandId]);

    // Strain prevalence -> legacy strainType
    let strainType = '';
    if (isDetail && attrs.strain && attrs.strain.prevalence) strainType = attrs.strain.prevalence.name;
    else if (attrIds.strainId && strains[attrIds.strainId]) strainType = strains[attrIds.strainId].prevalence || '';

    const variants = (item.variants || []).map((v) => mapVariant(item, v));
    const firstVariantCompounds = (item.variants || []).map((v) => v.compounds).find(Boolean) || {};

    const images = (item.images || []).map((im, i) => ({
        __typename: 'ProductImage',
        id: `${item.id}-img-${i}`,
        url: im.source,
        label: '',
        description: '',
    }));

    return {
        __typename: 'Product',
        id: item.id,
        slug: item.id, // Sweed has no slug; use id for routing during the test
        name: item.name,
        description: item.description || '',
        descriptionHtml: item.description || '',
        category,
        subcategory: '',
        strainType: strainType || '',
        image: item.images && item.images[0] ? item.images[0].source : null,
        images,
        brand,
        menuTypes: [],
        enterpriseProductId: '',
        productBatchId: '',
        posId: '',
        posMetaData: { __typename: 'PosMetaData', id: '', category: '', sku: '' },
        potencyThc: firstCompound(firstVariantCompounds.thc),
        potencyCbd: firstCompound(firstVariantCompounds.cbd),
        cannabinoids: [],
        terpenes: [],
        effects: isDetail ? (attrs.effects || []).map((e) => e.name) : [],
        tags: isDetail ? (attrs.tags || []).map((t) => t.name) : [],
        staffPick: false,
        variants,
    };
}

/**
 * Map a Sweed products-list response to the legacy `menu` object.
 */
export function mapSweedToMenu(data, saleType) {
    const rel = (data && data.relations) || {};
    const categories = {};
    (rel.categories || []).forEach((c) => { categories[c.id] = c.name; });
    const brands = {};
    (rel.brands || []).forEach((b) => { brands[b.id] = b; });
    const strains = {};
    (rel.strains || []).forEach((s) => { strains[s.id] = s; });

    const products = (data && data.items ? data.items : []).map((it) =>
        mapSweedToProduct(it, { saleType, categories, brands, strains })
    );

    return {
        __typename: 'Menu',
        productsCount: data && data.totalItems != null ? data.totalItems : products.length,
        products,
        brands: Object.values(brands).map((b) => mapBrand(b)),
        weights: [],
        tags: [],
    };
}

/**
 * Map a Sweed store to the legacy `retailer` object. Every retailerFragment key is
 * present (with __typename); operational data Sweed doesn't provide is null/[].
 */
export function mapSweedToRetailer(store, retailerId) {
    if (!store) return null;
    const loc = store.location || {};
    const contacts = store.contacts || {};
    const saleTypes = store.saleTypes || [];

    return {
        __typename: 'Retailer',
        id: retailerId || store.id,
        name: store.name,
        address: contacts.address || '',
        addressObject: {
            __typename: 'AddressObject',
            line1: contacts.address || '',
            line2: '',
            city: loc.city ? loc.city.name : '',
            postalCode: '',
            state: loc.region ? loc.region.name : '',
            country: '',
        },
        banner: null,
        coordinates: loc.coordinates
            ? { __typename: 'Coordinates', latitude: loc.coordinates.latitude, longitude: loc.coordinates.longitude }
            : null,
        deliverySettings: null,
        fulfillmentOptions: {
            __typename: 'FulfillmentOptions',
            pickup: true,
            delivery: false,
            curbsidePickup: false,
            driveThruPickup: false,
        },
        hours: {
            __typename: 'RetailerHours',
            regular: [],
            pickup: [],
            delivery: [],
            specialHours: [],
        },
        menuTypes: saleTypes.map((s) => s.toUpperCase()),
        paymentMethodsByOrderTypes: [],
        settings: { __typename: 'RetailerSettings', menuWeights: false },
    };
}
